import type { ComponentLeadershipWheelSubTrait } from '@edwin-group/people-graphql';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { useLeadershipWheelStore } from '../store/useLeadershipWheelStore';
import { sanitizeGradientId } from '../utils/sanitizeGradientId';

export function useCircleContainer() {
  const [positionWheel, setPositionWheel] = useState(-90);
  const { focusAreas, FocusArea, SubFocusArea, onUpdateFocusArea, onUpdateSubFocusArea, slices, onUpdateSlices } =
    useLeadershipWheelStore();
  const sliceCount = slices.length;
  const radius = 160;
  const gradient1From = FocusArea?.attributes?.typeSection.mainGradientFrom;
  const center = radius;

  const sliceAngle = 360 / sliceCount;
  const textRadius = radius * 0.6;

  useEffect(() => {
    setPositionWheel(-90);
  }, [slices]);

  const onClickSlice = async (startAngle: number, index: number) => {
    const targetAngle = -(startAngle + sliceAngle / 2) + 270;
    setPositionWheel(targetAngle);

    if (!FocusArea) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      const SubFocusAreaArray = focusAreas[index].attributes?.subFocusArea || [];
      onUpdateFocusArea(focusAreas[index]);
      onUpdateSlices(SubFocusAreaArray as ComponentLeadershipWheelSubTrait[], focusAreas[index]);
      setPositionWheel(-90);
    }

    if (FocusArea && !SubFocusArea) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      onUpdateSubFocusArea(FocusArea?.attributes?.subFocusArea?.[index] as ComponentLeadershipWheelSubTrait);
    }
  };

  const generateGradients = () => {
    return slices.map((gradient, index) => {
      const startAngle = sliceAngle * (index + 1) - 90;
      const endAngle = sliceAngle * (index + 1) - 90;

      const newCenter = center;
      const middle = (startAngle + endAngle) / 2;

      const gradientTransform = `rotate(${middle + 90} ${newCenter} ${newCenter})`;

      const gradientId = sanitizeGradientId(gradient.id);

      return (
        <linearGradient
          key={gradientId}
          id={gradientId}
          gradientUnits="userSpaceOnUse"
          x1={newCenter}
          y1={newCenter}
          x2={newCenter}
          y2={newCenter - radius}
          gradientTransform={gradientTransform}>
          <stop offset="0%" stopColor={gradient.end} />
          <stop offset="100%" stopColor={gradient.start} />
        </linearGradient>
      );
    });
  };

  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const generateSlices = () => {
    return slices.map((gradient, index) => {
      const startAngle = sliceAngle * index;
      const endAngle = sliceAngle * (index + 1);
      const gradientId = `url(#${sanitizeGradientId(gradient.id)})`;

      const x1 = center + radius * Math.cos(toRad(startAngle));
      const y1 = center + radius * Math.sin(toRad(startAngle));
      const x2 = center + radius * Math.cos(toRad(endAngle));
      const y2 = center + radius * Math.sin(toRad(endAngle));

      return (
        <motion.path
          key={index + (slices[index].focusArea || '')}
          onClick={() => onClickSlice(startAngle, index)}
          d={`M${center},${center} L${x1},${y1} A${radius},${radius} 0 0,1 ${x2},${y2} Z`}
          fill={gradientId}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: index * 0.1, duration: 1 }}
        />
      );
    });
  };

  const generateText = () => {
    const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
      const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
      return {
        x: centerX + radius * Math.cos(angleInRadians),
        y: centerY + radius * Math.sin(angleInRadians),
      };
    };

    const describeArc = (
      x: number,
      y: number,
      radius: number,
      startAngle: number,
      endAngle: number,
      sweepFlag: string,
    ) => {
      const start = polarToCartesian(x, y, radius, endAngle);
      const end = polarToCartesian(x, y, radius, startAngle);

      const largeArcFlag = Math.abs(endAngle - startAngle) <= 180 ? '0' : '1';

      const d = ['M', start.x, start.y, 'A', radius, radius, 0, largeArcFlag, sweepFlag, end.x, end.y].join(' ');

      return d;
    };

    const calculateFontSize = (text: string, arcRadius: number, angle: number): string => {
      const baseFontSize = 14;
      const textLength = text.length;
      const arcLength = (angle * Math.PI * arcRadius) / 180;
      const estimatedTextWidth = textLength * (baseFontSize * 0.55);

      if (estimatedTextWidth > arcLength * 0.85) {
        const scaleFactor = (arcLength * 0.85) / estimatedTextWidth;
        return `${Math.max(8, baseFontSize * scaleFactor)}px`;
      }

      return `${baseFontSize}px`;
    };

    return Array.from({ length: sliceCount }).map((_, index) => {
      const rotate: { [key: number]: number } = {
        7: 5,
        6: 10,
        5: 35,
        4: 0,
        3: -63,
        2: -179,
      };
      const angle = sliceAngle * (index + 1) + sliceAngle / 2;

      const curvedTextRadius = textRadius + 45;

      const angleExtent = sliceAngle;
      let startAngle = (rotate[sliceCount] || 0) + angle - angleExtent / 2;
      let endAngle = angle + angleExtent / 2;

      // Default sweep flag
      let sweepFlag = '0';

      if (
        index === 0 ||
        index === slices.length - 1 ||
        (slices.length >= 6 &&
          (index === 0 || index === 1 || index === slices.length - 1 || index === slices.length - 2))
      ) {
        [startAngle, endAngle] = [endAngle, startAngle];
        sweepFlag = '1';
      }

      const pathId = `textPath${slices[index].focusArea}`;
      const rotateAngle = sliceCount === 7 ? 30 : 0;
      const text = slices[index].focusArea || '';
      const fontSize = calculateFontSize(text, curvedTextRadius, angleExtent);

      const startOffset = sweepFlag === '1' ? 10 : -10;
      const endOffset = sweepFlag === '1' ? -10 : 10;

      const pathData = describeArc(
        center,
        center,
        curvedTextRadius,
        startAngle + rotateAngle + startOffset,
        endAngle + rotateAngle + endOffset,
        sweepFlag,
      );

      return (
        <React.Fragment key={slices[index].focusArea}>
          <defs>
            <path id={pathId} d={pathData} stroke="blue" strokeWidth="2" fill="none" />
          </defs>
          <text
            style={{
              fontSize,
              fill: 'black',
              fontWeight: '500',
              pointerEvents: 'none',
            }}>
            <textPath href={`#${pathId}`} startOffset="50%" textAnchor="middle">
              {text}
            </textPath>
          </text>
        </React.Fragment>
      );
    });
  };

  return {
    positionWheel,
    focusAreas,
    FocusArea,
    SubFocusArea,
    onUpdateSlices,
    radius,
    generateGradients,
    generateSlices,
    generateText,
    gradient1From,
  };
}
